/* eslint-disable security/detect-object-injection */

import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, Paper, Typography, Select, MenuItem, Button, TextField, CircularProgress } from '@mui/material'

import { loginTypes, ssnRequiredLoginTypes, ssnPlaceholders } from '@config/Constants'
import { handleLogin } from '@store/slices/Authentication.slice'
import { type RootState } from '@store/index'
import { checkSSnError } from '@util/CommonServicess'

import Logo from '@assets/images/Logo.svg'
import SEBLogo from '@assets/images/sebLogo.png'
import styles from './Login.styles'
import css from './Login.module.css'

const LoginPage: React.FC = () => {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()

    const [signingMethod, setSigningMethod] = useState('bankid-se')
    const [ssnError, setSsnError] = useState(false)

    const loginLoading = useSelector((state: RootState) => state.authentication.loginLoading)

    const ssnRef = useRef<any>(null)

    const dispatchLoginAction = (loginData: any): void => {
        dispatch(handleLogin({ ...loginData }))
    }

    const handleClickLogin = (): void => {
        const loginData = {
            signingMethod,
            langId: i18n.language,
            metadata: { 'ui-module': 'seb-recipient-dashboard' },
        }

        if (ssnRequiredLoginTypes.includes(signingMethod)) {
            const ssn = ssnRef?.current.value
            if (!ssn) {
                setSsnError(true)
            } else if (!checkSSnError(ssn, signingMethod)) {
                setSsnError(true)
            } else {
                const loginDataWithSSN = { ...loginData, ssn }
                dispatchLoginAction(loginDataWithSSN)
            }
        } else {
            dispatchLoginAction(loginData)
        }
    }

    const handleLoginTypeChange = (event: any): void => {
        setSsnError(false)
        setSigningMethod(event.target.value)
    }

    return (
        <Grid container justifyContent="center" sx={styles.container}>
            <Grid item container sx={styles.loginWrapper}>
                <img src={SEBLogo} style={styles.sebLogo} />
                <Paper sx={styles.paper} elevation={23}>
                    <img src={Logo} alt="logo" />
                    <Typography color="primary" variant="h2" sx={styles.headerText}>
                        {t('login.identityCheckTitle')}
                    </Typography>
                    <Typography variant="body1" sx={styles.description}>
                        {t('login.identityCheckDescription')}
                    </Typography>
                    <Typography variant="body1" sx={styles.label}>
                        {t('global.verified_secured')}:
                    </Typography>
                    <Select value={signingMethod} onChange={handleLoginTypeChange} sx={styles.loginTypeSelector}>
                        {loginTypes.map((lang, key) => (
                            <MenuItem key={key} value={lang.value}>
                                <Typography variant="body1">{lang.label}</Typography>
                            </MenuItem>
                        ))}
                    </Select>
                    {ssnRequiredLoginTypes.includes(signingMethod) && (
                        <>
                            <Typography variant="body1" sx={styles.label}>
                                {t('global.ssn')}:
                            </Typography>
                            <TextField
                                size="small"
                                placeholder={ssnPlaceholders[signingMethod]}
                                sx={styles.personNumberField}
                                error={ssnError}
                                helperText={ssnError && 'Please provide valid SSN!'}
                                inputRef={ssnRef}
                                onClick={() => {
                                    setSsnError(false)
                                }}
                            />
                        </>
                    )}
                    <Button variant="contained" size="small" sx={styles.loginButton} onClick={handleClickLogin}>
                        {!loginLoading ? (
                            t('global.next')
                        ) : (
                            <div className={css.buttonLoaderWrapper}>
                                <Typography variant="body2" sx={{ fontWeight: '600' }}>
                                    {t('global.loading')}
                                </Typography>
                                <CircularProgress color="secondary" size={15} sx={{ marginLeft: '8px' }} />
                            </div>
                        )}
                    </Button>
                    <Typography variant="subtitle1" sx={styles.noteText}>
                        {t('login.note')}
                    </Typography>
                </Paper>
            </Grid>
        </Grid>
    )
}

export default LoginPage
